import React, { useEffect, useState } from "react";
import { Card, Empty, Space, Spin, Alert, Tag, Typography, Row, Col, Pagination, Button, message } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined, CopyOutlined } from "@ant-design/icons";
import { useDatateamReprocess } from "../../hooks/useDatateamReprocess";
import PageHeader from "../../components/common/PageHeader";

const { Text } = Typography;

/**
 * Página de monitoramento de reprocessamento do DataTeam.
 *
 * Exibe todos os resultados do reprocessamento dos demandIds enviados em formato de cards com paginação.
 */
const DatateamReprocessMonitoringContent: React.FC = () => {
  const { demandIds, environment, idArtifact, results, reprocessDatateam, loading, error, setResults } = useDatateamReprocess();
  const [initialLoading, setInitialLoading] = useState<boolean>(false);
  const [processedDemandIdsKey, setProcessedDemandIdsKey] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  /**
   * Cria uma chave única baseada nos IDs para detectar quando os IDs mudam
   */
  const demandIdsKey = demandIds.join(",");

  /**
   * Efeito para executar o reprocessamento quando os IDs mudam.
   */
  useEffect(() => {
    const executeReprocess = async () => {
      // Se não há IDs ou ambiente, não faz nada
      if (demandIds.length === 0 || !environment) {
        return;
      }

      // Se os IDs mudaram, limpa resultados anteriores e executa novo reprocessamento
      if (demandIdsKey !== processedDemandIdsKey) {
        setResults(null); // Limpa resultados anteriores
        setProcessedDemandIdsKey(demandIdsKey); // Atualiza a chave processada

        setInitialLoading(true);
        try {
          await reprocessDatateam(demandIds, environment, idArtifact || 0);
        } catch (err) {
          console.error("Erro ao reprocessar:", err);
        } finally {
          setInitialLoading(false);
        }
      }
    };

    executeReprocess();
  }, [demandIds, demandIdsKey, processedDemandIdsKey, environment, idArtifact, reprocessDatateam, setResults]);

  // Resetar página quando os resultados mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [results]);

  if (initialLoading || loading) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <PageHeader title="Monitoramento de Reprocessamento" />
          <Spin size="large" style={{ display: "block", textAlign: "center", padding: "40px" }} />
        </Space>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <PageHeader title="Monitoramento de Reprocessamento" />
          <Alert message="Erro ao reprocessar" description={error} type="error" showIcon />
        </Space>
      </Card>
    );
  }

  if (demandIds.length === 0 || !environment) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <PageHeader title="Monitoramento de Reprocessamento" />
          <Empty description="Nenhum demandId para reprocessar. Volte para a página de Reprocessar DataTeam para adicionar demandIds." image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Space>
      </Card>
    );
  }

  const getStatusTag = (status?: string) => {
    if (!status) {
      return (
        <Tag color="default" icon={<InfoCircleOutlined />}>
          Sem status
        </Tag>
      );
    }
    if (status === "sendQueue" || status === "processed") {
      return (
        <Tag color="success" icon={<CheckCircleOutlined />}>
          {status}
        </Tag>
      );
    }
    if (status === "alreadyReprocess") {
      return (
        <Tag color="warning" icon={<InfoCircleOutlined />}>
          {status}
        </Tag>
      );
    }
    if (status === "error" || status.includes("error") || status.includes("Error")) {
      return (
        <Tag color="error" icon={<CloseCircleOutlined />}>
          {status}
        </Tag>
      );
    }
    return (
      <Tag color="processing" icon={<InfoCircleOutlined />}>
        {status}
      </Tag>
    );
  };

  if (!results || !results.data || results.data.length === 0) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <PageHeader title="Monitoramento de Reprocessamento" />
          <Empty description="Nenhum resultado disponível." image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Space>
      </Card>
    );
  }

  // Calcular itens da página atual
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentItems = results.data.slice(startIndex, endIndex);
  const total = results.data.length;

  // Função para copiar todos os IDs reprocessados em formato JSON array
  const copyAllIds = () => {
    if (!results || !results.data || results.data.length === 0) {
      message.warning("Nenhum ID para copiar");
      return;
    }

    const allIds = results.data.map((item) => item.id || item.idDemanda);
    const jsonArray = JSON.stringify(allIds);
    
    navigator.clipboard.writeText(jsonArray).then(() => {
      message.success(`${allIds.length} IDs copiados em formato JSON array`);
    }).catch(() => {
      message.error("Erro ao copiar IDs");
    });
  };

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <PageHeader 
          title="Monitoramento de Reprocessamento" 
          actions={
            results && results.data && results.data.length > 0 ? (
              <Button 
                icon={<CopyOutlined />} 
                onClick={copyAllIds}
                type="primary"
              >
                Copiar Todos os IDs
              </Button>
            ) : undefined
          }
        />
        <div>
          <Space direction="vertical" size="small" style={{ marginBottom: 16 }}>
            <Text type="secondary">
              Ambiente: <Text strong>{environment}</Text>
            </Text>
            <Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
              Total de demandIds processados: {total}
            </Text>
          </Space>
          <Row gutter={[16, 16]}>
            {currentItems.map((item, index) => (
              <Col key={`${item.id}-${index}`} xs={24} sm={24} md={24} lg={24} xl={24}>
                <Card size="small">
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", flexWrap: "wrap", gap: "8px" }}>
                      <div style={{ flex: "1", minWidth: "200px" }}>
                        <Text strong style={{ marginRight: 8 }}>
                          Demand ID:
                        </Text>
                        <Text code copyable style={{ fontSize: "14px" }}>
                          {item.id || item.idDemanda}
                        </Text>
                      </div>
                      <div style={{ flexShrink: 0 }}>{getStatusTag(item.status)}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-start", width: "100%", flexWrap: "wrap", gap: "8px" }}>
                      <Text strong style={{ marginRight: 8, flexShrink: 0 }}>
                        Mensagem:
                      </Text>
                      <Text copyable ellipsis={{ tooltip: item.message }} style={{ flex: "1", minWidth: "200px", wordBreak: "break-word" }}>
                        {item.message}
                      </Text>
                    </div>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={total}
              showSizeChanger
              showTotal={(total, range) => `${range[0]}-${range[1]} de ${total} demandIds`}
              pageSizeOptions={["25", "50", "100", "200", "500"]}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
              onShowSizeChange={(current, size) => {
                setCurrentPage(1);
                setPageSize(size);
              }}
            />
          </div>
        </div>
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página DatateamReprocessMonitoring.
 */
const DatateamReprocessMonitoring: React.FC = () => {
  return (
    <DatateamReprocessMonitoringContent />
  );
};

export default DatateamReprocessMonitoring;

